/**
 * `<SavedFilterPicker>` — tick saved filters to apply them together.
 *
 * A tree rather than a list, because a filter library grows past a screenful
 * fast and a flat list of thirty is a list nobody reads. One level of grouping:
 * that is what a set of filters needs, and arbitrary depth is a hierarchy to
 * maintain for no gain.
 *
 * Ticking several applies all of them, ANDed — see `saved-filters.ts` for why
 * that and not OR.
 */

import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { SavedScreenerFilter } from "@qufolio/model";

import { FAVORITES, groupByCategory, groupBySource, isSealed, type FilterCategory } from "../lib/saved-filters.js";
import { conditionLinkKey, linkLabel, type ScreenLinks } from "../lib/screen-links.js";
import { ConditionPresetList, type ConditionPresetsBinding } from "./ConditionPresetList.js";

export interface SavedFilterPickerProps {
  filters: readonly SavedScreenerFilter[];
  /**
   * The screen's category order (`savedFilterCategoryOrder`). Categories it
   * names come first in that order; the rest follow A–Z.
   */
  categoryOrder?: readonly string[];
  /** Currently applied, by id. */
  selected: readonly string[];
  onChange: (selectedIds: string[]) => void;
  /** Dismiss. Omitted with `inline`, where there is nothing to dismiss. */
  onClose?: () => void;
  /**
   * Rendered in place rather than as a popover.
   *
   * Drops the three things that only make sense floating: the outside-click
   * dismiss (which would fire on every click in the panel beside it), the ✕,
   * and the shadow and fixed width. Everything else — ticking several, the
   * search, the category tree, the ⋮ menu — is what a docked list wants too,
   * so the alternative was a second component that would drift from this one.
   */
  inline?: boolean;
  /** Summarise a filter for its tooltip. */
  describe?: (filter: SavedScreenerFilter) => string;
  /** Open the editor on this filter. Omitted, the ⋮ menu offers no edit. */
  onEdit?: (filter: SavedScreenerFilter) => void;
  onDelete?: (filter: SavedScreenerFilter) => void;
  /** Start a new filter. Omitted, there is no add button. */
  onAdd?: () => void;
  /**
   * Flip a condition's ★. Omitted — a screen this caller cannot edit — a
   * favourite still shows its filled star and nothing else does.
   */
  onToggleFavorite?: (filter: SavedScreenerFilter) => void;
  /** Move a category one place. Omitted, the group headers have no arrows. */
  onMoveCategory?: (category: string, delta: -1 | 1) => void;
  /**
   * Who a sealed condition's rule belongs to, as a reader names them (`JP`) —
   * the screen owner's display name, for the 🔒's hover text. Absent reads
   * "The group's rule".
   */
  sealedBy?: string;
  /**
   * What linked conditions are linked from, and which resolved to nothing
   * (`lib/screen-links.ts`). A linked row reads `🔗 JP · Persons`, is ticked and
   * removed and nothing else — no edit, and no Unlink, which would copy a rule.
   */
  links?: Pick<ScreenLinks, "describe" | "missing">;
  /**
   * The 📌 in the header (owner, 2026-10-26): pin the panel open beside the
   * toolbar strip. Pinned, it renders as `inline` does — no outside-click
   * dismiss, no ✕ — and fills the column the host gives it. Omitted (a phone),
   * there is no pin.
   */
  pin?: { pinned: boolean; onToggle: () => void };
  /**
   * Standard conditions offered under the empty state, each with + Add
   * (`ConditionPresetList`). Omitted, the empty state is the sentence alone.
   */
  presets?: ConditionPresetsBinding;
}

/**
 * The 🔒's hover text (`docs/plans/screen-links.md`, the mock). Says whose rule
 * and where it runs, and nothing about what it is.
 */
export function sealedHint(sealedBy: string | undefined): string {
  return `${sealedBy ? `${sealedBy}'s` : "The group's"} rule — applied on the server`;
}

export function SavedFilterPicker({
  filters,
  categoryOrder,
  selected,
  onChange,
  onClose,
  describe,
  onEdit,
  onDelete,
  onAdd,
  onToggleFavorite,
  onMoveCategory,
  sealedBy,
  links,
  inline: inlineProp = false,
  pin,
  presets,
}: SavedFilterPickerProps) {
  const inline = inlineProp || !!pin?.pinned;
  /** Hover drives the quiet controls: a hollow ☆ and a group's ↑↓ show only here. */
  const [hoverRow, setHoverRow] = useState<string | null>(null);
  const [hoverGroup, setHoverGroup] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  /** Which row's ⋮ menu is open. One at a time, by id. */
  const [menuFor, setMenuFor] = useState<string | null>(null);

  const needle = query.trim().toLowerCase();
  const groups = useMemo(() => {
    const matching = needle
      ? filters.filter(
          (f) =>
            f.name.toLowerCase().includes(needle) ||
            (f.category ?? "").toLowerCase().includes(needle),
        )
      : filters;
    return groupByCategory(matching, categoryOrder);
  }, [filters, needle, categoryOrder]);

  /** Group, then Category — only once something is linked (`groupBySource`). */
  const sourced = useMemo(() => {
    const matching = needle ? groups.flatMap((g) => (g.category === FAVORITES ? [] : g.filters)) : filters;
    return groupBySource(matching, categoryOrder, (f) =>
      f.link ? (links?.describe(f.link.screen)?.ownerName ?? f.link.screen) : undefined,
    );
  }, [groups, filters, needle, categoryOrder, links]);

  /** The linked condition whose ✕ is asking "Remove …?", by id. */
  const [confirming, setConfirming] = useState<string | null>(null);
  const confirmRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!confirming) return;
    // A press anywhere else, or Esc, is a Cancel.
    const onDown = (event: PointerEvent) => {
      if (!confirmRef.current?.contains(event.target as Node)) setConfirming(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setConfirming(null);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [confirming]);

  const toggle = (id: string) => {
    const next = selected.includes(id)
      ? selected.filter((s) => s !== id)
      : [...selected, id];
    onChange(next);
  };

  const toggleGroup = (category: string) => {
    setCollapsed((c) => {
      const next = new Set(c);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  /**
   * Dismiss on a click outside.
   *
   * This panel had none at all: it closed from its own × and from the toolbar
   * button that opened it, and clicking anywhere else left it sitting over the
   * grid it was filtering.
   *
   * `pointerdown` rather than `mousedown`, and capture rather than bubble. The
   * chart calls `preventDefault()` on its own pointer events, which suppresses
   * the compatibility mouse events entirely — so a `mousedown` listener never
   * fires for a click on the candles, which is a very ordinary place to click
   * next. Capture, because a handler inside the panel that stops propagation
   * would otherwise hide the outside click that follows it.
   */
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Docked, there is no outside: a click in the conditions beside this would
    // "dismiss" a panel that is part of the page.
    if (inline || !onClose) return;
    const onDown = (event: PointerEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) onClose();
    };
    // Deferred a tick: the very click that opened this panel is still being
    // dispatched, and without the wait the panel would close on its own opening.
    const id = setTimeout(() => document.addEventListener("pointerdown", onDown, true), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [onClose, inline]);

  /**
   * One category's header and rows. `scope` keys its collapse apart from a
   * category of the same name in another group; `own` is whether the screen's
   * ↑↓ order applies; `tagged` shows each linked row's group tag (★ Favourites,
   * where groups mix).
   */
  const renderGroup = (group: FilterCategory, scope: string, own: boolean, tagged: boolean) => {
    // Scoped, so JP's Weekly and this screen's Weekly fold separately.
    const ck = scope ? `${scope}${group.category}` : group.category;
    const isCollapsed = collapsed.has(ck) && !needle;
    const ticked = group.filters.filter((f) => selected.includes(f.id)).length;
    return (
      <div key={group.category === FAVORITES ? "__favorites" : ck || "__ungrouped"}>
        <div
          style={groupHeader}
          onMouseEnter={() => setHoverGroup(ck)}
          onMouseLeave={() => setHoverGroup(null)}
        >
          <button
            onClick={() => toggleGroup(ck)}
            style={groupRow}
            aria-expanded={!isCollapsed}
          >
            <span style={{ width: "0.8rem" }}>{isCollapsed ? "▸" : "▾"}</span>
            <span style={{ flex: 1, textAlign: "left" }}>
              {group.category === FAVORITES ? "★ Favourites" : group.category || "Ungrouped"}
            </span>
            {/* The count is what makes a collapsed group readable: it says
                whether anything inside is applied without expanding it. */}
            <span style={{ color: "var(--qf-text-muted)" }}>
              {ticked > 0 ? `${ticked}/${group.filters.length}` : group.filters.length}
            </span>
          </button>
          {/* Outside the toggle, which is a button: a click here moves the
              group and must not collapse it. ★ Favourites is always first
              and Ungrouped always last, so neither has arrows. */}
          {onMoveCategory &&
            own &&
            !needle &&
            hoverGroup === ck &&
            group.category !== FAVORITES &&
            group.category !== "" && (
              <span style={moveButtons}>
                <button
                  aria-label={`Move ${group.category} up`}
                  onClick={() => onMoveCategory(group.category, -1)}
                  style={moveButton}
                >
                  ↑
                </button>
                <button
                  aria-label={`Move ${group.category} down`}
                  onClick={() => onMoveCategory(group.category, 1)}
                  style={moveButton}
                >
                  ↓
                </button>
              </span>
            )}
        </div>

        {!isCollapsed &&
          group.filters.map((filter) => {
            // A sealed condition is ticked and nothing else: no rule to
            // describe, so no tooltip of one, and no ⋮ — Edit would open
            // the builder on a rule this caller does not hold, and Delete
            // would drop the group's condition from the panel's copy of
            // a library that is the group's.
            const sealed = isSealed(filter);
            // A linked one is evaluated on the server whatever it shows,
            // so its hover names where the rule lives, never the rule.
            const link = filter.link;
            const from = link ? linkLabel(links, link.screen) : "";
            const owner = link ? links?.describe(link.screen)?.ownerName : sealedBy;
            const gone = !!link && !!links?.missing.has(conditionLinkKey(filter));
            const hint = gone
              ? `No longer in ${links?.describe(link!.screen)?.name ?? link!.screen} — not applied`
              : link
                ? sealed
                  ? `Linked from ${from} · ${sealedHint(owner)}`
                  : `Linked from ${from} — applied on the server`
                : sealed
                  ? sealedHint(sealedBy)
                  : describe?.(filter);
            return (
            <Fragment key={filter.id}>
            <label
              onMouseEnter={() => setHoverRow(filter.id)}
              onMouseLeave={() => setHoverRow(null)}
              title={hint}
              style={{
                ...filterRow,
                background: selected.includes(filter.id)
                  ? "var(--qf-selection)"
                  : "transparent",
              }}
            >
              <input
                type="checkbox"
                checked={selected.includes(filter.id)}
                onChange={() => toggle(filter.id)}
              />
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  ...(gone ? { color: "var(--qf-text-muted)" } : {}),
                }}
              >
                {filter.name}
                {gone && ` — no longer in ${links?.describe(link!.screen)?.name ?? link!.screen}`}
              </span>
              {/* The group's tag, not `🔗 JP · Persons`: under its group the
                  header says it, so only ★ Favourites — where groups mix —
                  tags the row. Hover still names the screen. */}
              {tagged && link && !gone && <span style={ownerTag}>{owner ?? from}</span>}
              {gone && (
                <span aria-hidden="true" style={{ fontSize: "0.7rem", color: "var(--qf-warn)" }}>
                  ⚠
                </span>
              )}
              {sealed && !gone && (
                <span
                  role="img"
                  aria-label={sealedHint(owner)}
                  title={sealedHint(owner)}
                  style={{ fontSize: "0.7rem", color: "var(--qf-text-muted)" }}
                >
                  🔒
                </span>
              )}
              {/* A link is removed, never edited or unlinked: the rule is
                  its source screen's, and a copy would be one this reader
                  may not hold. */}
              {link && onDelete && (
                <button
                  aria-label={`Remove ${filter.name}`}
                  title="Remove this link from the screen"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Asks first (owner, 2026-11-01): one ✕ beside a column
                    // of them is easy to hit by mistake.
                    setConfirming(filter.id);
                  }}
                  style={dotsButton}
                >
                  {gone ? "Remove" : "✕"}
                </button>
              )}
              {/* A filled star always shows; a hollow one only on hover and
                  only where it can be clicked — the owner's mock. */}
              {onToggleFavorite && (filter.favorite || hoverRow === filter.id) ? (
                <button
                  aria-label={`${filter.favorite ? "Remove" : "Add"} ${filter.name} ${
                    filter.favorite ? "from" : "to"
                  } favourites`}
                  aria-pressed={!!filter.favorite}
                  onClick={(e) => {
                    // The row is a label: without these the star would
                    // also tick the condition it is marking.
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleFavorite(filter);
                  }}
                  style={{ ...starButton, color: filter.favorite ? "var(--qf-warn)" : "var(--qf-text-muted)" }}
                >
                  {filter.favorite ? "★" : "☆"}
                </button>
              ) : filter.favorite ? (
                <span aria-label="Favourite" style={{ ...starButton, color: "var(--qf-warn)" }}>
                  ★
                </span>
              ) : null}
              {!sealed && !link && (onEdit || onDelete) && (
                <span style={{ position: "relative" }}>
                  <button
                    aria-label={`Actions for ${filter.name}`}
                    onClick={(e) => {
                      // The row is a label wrapping a checkbox, so a click
                      // anywhere in it toggles the filter — the menu must
                      // not also apply what it is about to edit.
                      e.preventDefault();
                      e.stopPropagation();
                      setMenuFor((id) => (id === filter.id ? null : filter.id));
                    }}
                    style={dotsButton}
                  >
                    ⋮
                  </button>
                  {menuFor === filter.id && (
                    <span style={rowMenu}>
                      {onEdit && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setMenuFor(null);
                            onEdit(filter);
                          }}
                          style={menuItem}
                        >
                          Edit
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setMenuFor(null);
                            // Asks first, like a link's ✕ (owner, 2026-11-01).
                            setConfirming(filter.id);
                          }}
                          style={{ ...menuItem, color: "var(--qf-negative)" }}
                        >
                          Delete
                        </button>
                      )}
                    </span>
                  )}
                </span>
              )}
            </label>
            {confirming === filter.id && onDelete && (
              <div
                ref={confirmRef}
                role="alertdialog"
                aria-label={`${link ? "Remove" : "Delete"} ${filter.name}?`}
                style={confirmBox}
              >
                <div>
                  {link ? "Remove" : "Delete"} <b>{filter.name}</b>
                  {link ? " from this screen" : ""}?
                </div>
                {link && (
                  <div style={{ color: "var(--qf-text-muted)", fontSize: "0.68rem", margin: "0.15rem 0 0.3rem" }}>
                    It stays on {from}. Link it again from Link from screen….
                  </div>
                )}
                <span style={{ display: "flex", gap: "0.3rem" }}>
                  <button
                    onClick={() => {
                      setConfirming(null);
                      // Unticked as it goes: a removed condition must not keep filtering.
                      if (selected.includes(filter.id)) onChange(selected.filter((s) => s !== filter.id));
                      onDelete(filter);
                    }}
                    style={confirmRemove}
                  >
                    {link ? "Remove" : "Delete"}
                  </button>
                  <button autoFocus onClick={() => setConfirming(null)} style={confirmCancel}>
                    Cancel
                  </button>
                </span>
              </div>
            )}
            </Fragment>
            );
          })}
      </div>
    );
  };

  return (
    <div
      ref={panelRef}
      style={inline ? { ...panel, ...panelInline } : panel}
      role={inline ? "group" : "dialog"}
      aria-label="Saved conditions"
    >
      <div style={header}>
        <strong style={{ fontSize: "0.78rem" }}>Conditions</strong>
        {selected.length > 0 && (
          <button onClick={() => onChange([])} style={{ ...clearButton, marginLeft: 0 }}>
            Clear {selected.length}
          </button>
        )}
        {onAdd && (
          <button onClick={onAdd} title="New condition" style={addButton}>
            + New
          </button>
        )}
        {pin && (
          <button
            onClick={pin.onToggle}
            aria-label={pin.pinned ? "Unpin conditions" : "Pin conditions beside the toolbar"}
            aria-pressed={pin.pinned}
            title={pin.pinned ? "Unpin" : "Pin beside the toolbar"}
            style={{
              ...clearButton,
              ...(onAdd ? { marginLeft: 0 } : {}),
              ...(pin.pinned
                ? { background: "var(--qf-accent)", color: "var(--qf-background)", borderColor: "var(--qf-accent)" }
                : {}),
            }}
          >
            📌
          </button>
        )}
        {!inline && onClose && (
          <button onClick={onClose} aria-label="Close saved conditions" style={{ ...clearButton, ...(pin || onAdd ? { marginLeft: 0 } : {}) }}>
            ✕
          </button>
        )}
      </div>

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search conditions…"
        aria-label="Search saved conditions"
        style={search}
      />

      <div style={list}>
        {groups.length === 0 && (
          <div style={{ padding: "0.5rem", color: "var(--qf-text-muted)" }}>
            {filters.length === 0
              ? presets
                ? "No saved conditions yet. Add a preset below, or make one with + New."
                : "No saved conditions yet — save one from the screener editor."
              : "Nothing matches."}
          </div>
        )}
        {/* Under the empty state only (owner, 2026-10-26): a new screen starts
            with something to tick. Once the library has conditions, presets
            live in the editor's Conditions tab. */}
        {filters.length === 0 && presets && (
          <div style={{ borderTop: "1px solid var(--qf-border)", paddingTop: "0.3rem" }}>
            <div style={{ ...groupRow, cursor: "default", paddingLeft: "0.3rem" }}>Presets</div>
            <ConditionPresetList {...presets} />
          </div>
        )}

        {sourced === null
          ? groups.map((group) => renderGroup(group, "", true, false))
          : (
            <>
              {sourced.favorites.length > 0 &&
                renderGroup({ category: FAVORITES, filters: sourced.favorites }, "", false, true)}
              {sourced.sources.map((source) => {
                const key = `${source.owner}`;
                const shut = collapsed.has(key) && !needle;
                const all = source.categories.flatMap((c) => c.filters);
                const ticked = all.filter((f) => selected.includes(f.id)).length;
                return (
                  <div key={key}>
                    <button
                      onClick={() => toggleGroup(key)}
                      style={sourceRow}
                      aria-expanded={!shut}
                    >
                      <span style={{ width: "0.8rem" }}>{shut ? "▸" : "▾"}</span>
                      {source.owner ? (
                        <>
                          <span style={ownerTag}>{source.owner}</span>
                          <span style={{ flex: 1, textAlign: "left", color: "var(--qf-text-muted)", fontWeight: 400 }}>linked</span>
                        </>
                      ) : (
                        <span style={{ flex: 1, textAlign: "left" }}>This screen</span>
                      )}
                      <span style={{ color: "var(--qf-text-muted)", fontWeight: 400 }}>
                        {ticked > 0 ? `${ticked}/${all.length}` : all.length}
                      </span>
                    </button>
                    {!shut && (
                      <div style={{ paddingLeft: "0.6rem" }}>
                        {source.categories.map((group) => renderGroup(group, source.owner, source.owner === "", false))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
      </div>

      {selected.length > 1 && (
        <div style={footnote}>
          {selected.length} filters, all of which must match.
        </div>
      )}
    </div>
  );
}

const panel: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  width: 260,
  // Nearly full height, because a filter library is long and a short popover
  // turns picking one into scrolling.
  maxHeight: "min(80vh, 720px)",
  padding: "0.4rem",
  background: "var(--qf-surface-alt)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  boxShadow: "0 8px 28px rgba(0,0,0,0.45)",
  color: "var(--qf-text)",
  fontSize: "0.76rem",
};

/**
 * Docked: fills the column it is given, and drops the chrome that says "this
 * floats above the page".
 */
const panelInline: CSSProperties = {
  width: "100%",
  height: "100%",
  maxHeight: "none",
  boxShadow: "none",
  borderRadius: 0,
  border: "none",
  borderRight: "1px solid var(--qf-border)",
  background: "transparent",
};

const header: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  marginBottom: "0.35rem",
};

const clearButton: CSSProperties = {
  marginLeft: "auto",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0 0.35rem",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  fontSize: "0.68rem",
  cursor: "pointer",
};

const search: CSSProperties = {
  padding: "0.15rem 0.35rem",
  marginBottom: "0.35rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
  fontSize: "0.74rem",
};

const list: CSSProperties = { flex: 1, minHeight: 0, overflowY: "auto" };

const groupHeader: CSSProperties = { display: "flex", alignItems: "center" };
const moveButtons: CSSProperties = { display: "flex", gap: 2, paddingRight: 4 };
const moveButton: CSSProperties = {
  background: "none",
  border: "none",
  color: "var(--qf-text-muted)",
  cursor: "pointer",
  padding: "0 3px",
  font: "inherit",
};
const starButton: CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "0 4px",
  font: "inherit",
};
const groupRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.3rem",
  width: "100%",
  padding: "0.15rem 0.2rem",
  marginTop: "0.15rem",
  border: "none",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  cursor: "pointer",
};

const filterRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.35rem",
  padding: "0.15rem 0.2rem 0.15rem 1rem",
  borderRadius: 3,
  cursor: "pointer",
};

const addButton: CSSProperties = {
  marginLeft: "auto",
  border: "none",
  borderRadius: 4,
  padding: "0.1rem 0.45rem",
  background: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontSize: "0.68rem",
  fontWeight: 600,
  cursor: "pointer",
};

/** A linked group's tag — `JP` — in its header, and on a row in ★ Favourites. */
const ownerTag: CSSProperties = {
  fontSize: "0.62rem",
  fontWeight: 700,
  padding: "0 0.3rem",
  borderRadius: 3,
  lineHeight: "1rem",
  whiteSpace: "nowrap",
  textTransform: "none",
  letterSpacing: 0,
  background: "color-mix(in srgb, var(--qf-accent) 22%, transparent)",
  color: "var(--qf-accent)",
};

/** A group's own header — This screen, JP, JAS — above its categories. */
const sourceRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.3rem",
  width: "100%",
  padding: "0.2rem 0.2rem",
  marginTop: "0.3rem",
  border: "none",
  background: "transparent",
  color: "var(--qf-text)",
  fontSize: "0.72rem",
  fontWeight: 700,
  cursor: "pointer",
};

const confirmBox: CSSProperties = {
  margin: "0.1rem 0.2rem 0.3rem 1rem",
  padding: "0.35rem 0.45rem",
  border: "1px solid var(--qf-negative)",
  borderRadius: 4,
  background: "color-mix(in srgb, var(--qf-negative) 10%, transparent)",
  fontSize: "0.72rem",
};

const confirmRemove: CSSProperties = {
  border: "1px solid var(--qf-negative)",
  borderRadius: 4,
  padding: "0.05rem 0.5rem",
  background: "var(--qf-negative)",
  color: "var(--qf-background)",
  fontSize: "0.7rem",
  cursor: "pointer",
};

const confirmCancel: CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0.05rem 0.5rem",
  background: "transparent",
  color: "var(--qf-text)",
  fontSize: "0.7rem",
  cursor: "pointer",
};

const dotsButton: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--qf-text-muted)",
  cursor: "pointer",
  padding: "0 0.15rem",
  fontSize: "0.85rem",
  lineHeight: 1,
};

const rowMenu: CSSProperties = {
  position: "absolute",
  top: "100%",
  right: 0,
  zIndex: 5,
  display: "flex",
  flexDirection: "column",
  minWidth: 90,
  padding: "0.15rem",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
};

const menuItem: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--qf-text)",
  textAlign: "left",
  padding: "0.15rem 0.35rem",
  fontSize: "0.72rem",
  cursor: "pointer",
};

const footnote: CSSProperties = {
  padding: "0.3rem 0.2rem 0",
  borderTop: "1px solid var(--qf-border)",
  color: "var(--qf-text-muted)",
  fontSize: "0.68rem",
};
